import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import _ from "lodash";
import { Box, makeStyles, Button } from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import { useHistory, useLocation } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import ArrowBackIosRoundedIcon from "@material-ui/icons/ArrowBackIosRounded";
import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import { useLearnProcess } from "../../../../Providers/Data/LearnProcessProvider.jsx";
import AnswerUserFilesProvider from "../../../../Providers/Data/AnswerUserFilesProvider.jsx";
import GreyButton from "../../../../SharedComponents/GreyButton.jsx";
import ProgressTypography from "../../../../SharedComponents/ProgressTypography.jsx";
import PrimaryRoundedButton from "../../../../SharedComponents/PrimaryRoundedButton.jsx";
import Exercise9 from "./Exercise9.jsx";
import Exercise8 from "./Exercise8.jsx";
import Exercise7 from "./Exercise7.jsx";
import Exercise3 from "./Exercise3.jsx";
import Exercise5 from "./Exercise5.jsx";
import AddPointsAnimation from "../SharedComponents/AddPointsAnimation.jsx";
import FlexboxVertical from "../../../../SharedComponents/FlexboxVertical.jsx";
import Exercise11 from "./Exercise11.jsx";
import WrongAnswerAlertComment from "./SharedComponents/WrongAnswerAlertComment.jsx";
import { useCourseProvider } from "../../../../Providers/Data/CourseProvider.jsx";
import ExamInfoBlock from "./ExamInfoBlock.jsx";
import Exercise6 from "./Exercise6.jsx";
import { useDialogs } from "../../../../Providers/Common/DialogsProvider.jsx";
import Exercise12 from "./Exercise12.jsx";
import Exercise13 from "./Exercise13.jsx";
import CorrectAnswer from "./SharedComponents/CorrectAnswer.jsx";

const useStyles = makeStyles(
  () => ({
    button: {
      minWidth: ({ minW }) => minW,
      marginBottom: ({ isHiddenCheckButton }) => (isHiddenCheckButton ? 10 : 0),
    },
    arrowButtons: {
      position: "absolute",
      width: 50,
      height: 50,
      minWidth: "unset",
    },
    nextButton: {
      right: 0,
    },
    exerciseLearnStatusIcon: {
      position: "absolute",
      bottom: 100,
      right: 10,
    },
    colorPink: {
      color: "#EB4893",
    },
  }),
  {
    name: "makeExercisesStyles",
  },
);

/**
 * @param {ExerciseResponse} exercise
 */
const DefaultInfoBlock = ({ exercise }) => {
  const t = useTranslate();
  const progressText = `${exercise.deck.learnedCount} ${t("of")} ${exercise.deck.count}`;

  return (
    <FlexboxVertical alignItems="center">
      <ProgressTypography color="primary">{progressText}</ProgressTypography>
      <Typography color="primary">{t("completed")}</Typography>
    </FlexboxVertical>
  );
};

DefaultInfoBlock.propTypes = {
  exercise: PropTypes.object.isRequired,
};

/**
 * Управляет выполнением упражнений и просмотром правильных ответов завершённого экзамена.
 */
const Exercises = () => {
  const t = useTranslate();
  const history = useHistory();
  const location = useLocation();
  const { isViewExamDeck } = location.state || {};
  const { warn, success, info, alert } = useDialogs();
  const { currentDeck } = useCourseProvider();
  const {
    exercise,
    patchExercise,
    skipExercise,
    userIsWrong,
    requestOtherExercise,
    specifiedExerciseId,
  } = useLearnProcess();

  const [isHiddenCheckButton, setHiddenCheckButton] = useState(false);
  const classes = useStyles({ minW: _.get(exercise, "deck.exam.endDate") !== null ? 280 : 305, isHiddenCheckButton });
  const [isCheckButtonDisabled, setCheckButtonDisabled] = useState(true);
  const [virtualClick, setVirtualClick] = useState(false);
  const checkAnswerRef = useRef(/** @type {Function} */ null);

  const [attemptCount, setAttemptCount] = useState(1);

  const exercisesAttempts = exercise ? exercise.exercise.exercisesAttempts : 0;
  const homeworkUserData = _.get(exercise, "exercise.data.homework.userData");

  // Просмотр ответа доступен всем компаниям после завершения экзамена, включая ожидание проверки домашнего задания.
  const canShowRightAnswer =
    ["finished_passed", "finished_not_passed", "finished_wait_homework"].includes(exercise?.deck?.exam?.status) &&
    [3, 5, 8, 9, 11, 13].includes(exercise?.exercise?.type);

  /**
   * Открывает правильный ответ и закрывается без отправки результата или перехода к следующему упражнению.
   */
  const showExamRightAnswer = () => {
    if (!canShowRightAnswer) return;
    alert({
      title: t("Exercises.rightAnswer"),
      content: <CorrectAnswer exercise={exercise.exercise} />,
      confirmText: t("closeButton"),
      maxWidth: "sm",
    });
  };

  // В режиме сдачи экзамена повторно невозможно ответить (выбрать ответы), тогда disabledChoice === true
  const [disabledChoice, setDisabledChoice] = useState(false);

  //reset state
  useEffect(() => {
    setCheckButtonDisabled(true);
    setAttemptCount(1);
    if (exercise?.deck?.exam && (exercise?.exercise?.passed || exercise.deck?.exam?.endDate !== null)) {
      setDisabledChoice(true);
    } else {
      setDisabledChoice(false);
    }
  }, [exercise]);

  //скрыть кнопку ответа в зависимости от настроек
  useEffect(() => {
    if (exercise?.exercise?.data?.answer?.oneClickAnswer) {
      setHiddenCheckButton(true);
    } else {
      setHiddenCheckButton(false);
    }
  }, [exercise]);

  //виртуальный клик на кнопку ответа при нажатии на любой ответ при isHiddenCheckButton === true
  useEffect(() => {
    if (isHiddenCheckButton && !isCheckButtonDisabled && virtualClick) {
      checkAnswerButtonHandler().then();
      setVirtualClick(false);
    }
  }, [isHiddenCheckButton, isCheckButtonDisabled, virtualClick]);

  /**
   * @param {React.ReactNode} component
   * @param {string} answerValue
   * @param {Object} [answerData] - Полный ответ для повторной проверки на сервере.
   */
  const showRightAnswer = async (component, answerValue, answerData) => {
    const newAttemptCount = attemptCount + 1;
    const _attemptsOverflow = exercisesAttempts !== 0 && newAttemptCount > exercisesAttempts;
    // В экзаменационном режиме правильный ответ не показываем.
    // PATCH с результатом отправляется через patchExercise в checkAnswerButtonHandler,
    // поэтому здесь userIsWrong не вызываем, чтобы избежать дублирования запросов.
    // Уведомление тоже не показываем — его покажет checkAnswerButtonHandler.
    if (exercise.deck.exam) {
      setAttemptCount(newAttemptCount);
      return;
    }
    warn(t("Exercises.wrongAnswer"));
    if (_attemptsOverflow) {
      alert({
        title: t("Exercises.rightAnswerWas"),
        content: component,
        confirmText: t("Exercises.next"),
        maxWidth: "sm",
      });
      userIsWrong(answerValue, answerData).then();
    }
    setAttemptCount(newAttemptCount);
  };

  /**
   * @param {React.ReactNode} component
   * @param {[String]} comments
   * @param {string} answerValue
   * @param {Object} [answerData] - Полный ответ для повторной проверки на сервере.
   */
  const showRightAnswerWithComments = async (component, comments, answerValue, answerData) => {
    const newAttemptCount = attemptCount + 1;
    const _attemptsOverflow = exercisesAttempts !== 0 && newAttemptCount > exercisesAttempts;
    // В экзаменационном режиме правильный ответ не показываем.
    // Комментарии к ответу показываем только если включена опция showAnswerComments.
    // PATCH с результатом отправляется через patchExercise в checkAnswerButtonHandler,
    // поэтому здесь userIsWrong не вызываем, чтобы избежать дублирования запросов.
    // Уведомление тоже не показываем — его покажет checkAnswerButtonHandler.
    if (exercise.deck.exam) {
      if (comments.length > 0 && exercise.deck.exam.showAnswerComments) {
        alert({
          content: <WrongAnswerAlertComment comments={comments} />,
          confirmText: t("Exercises.next"),
          maxWidth: "sm",
        });
      }
      setAttemptCount(newAttemptCount);
      return;
    }
    if (!component) {
      if (comments.length > 0) {
        alert({
          content: <WrongAnswerAlertComment comments={comments} />,
          confirmText: t("Exercises.next"),
          maxWidth: "sm",
        });
      }
      setAttemptCount(newAttemptCount);
      return;
    }
    warn(t("Exercises.wrongAnswer"));
    if (_attemptsOverflow) {
      alert({
        title: t("Exercises.rightAnswerWas"),
        content: component,
        confirmText: t("Exercises.next"),
        maxWidth: "sm",
      });
      userIsWrong(answerValue, answerData).then();
    } else {
      if (comments.length > 0) {
        alert({
          content: <WrongAnswerAlertComment comments={comments} />,
          confirmText: t("Exercises.next"),
          maxWidth: "sm",
        });
      }
    }
    setAttemptCount(newAttemptCount);
  };

  /** */
  const getExerciseContent = () => {
    const props1 = {
      checkAnswerRef,
      exercise: exercise.exercise.data,
      setCheckButtonDisabled,
      isHiddenCheckButton,
      exerciseTitle: exercise.exercise.title,
      disabledChoice,
      isExam: !!exercise.deck.exam,
    };
    const props2 = {
      ...props1,
      showRightAnswer,
    };
    const propsWithComments = {
      ...props1,
      showRightAnswer,
      showRightAnswerWithComments,
      setVirtualClick,
      isExam: !!exercise.deck.exam,
    };
    switch (exercise.exercise.type) {
      case 13:
        return <Exercise13 {...props2} />;
      case 12:
        return <Exercise12 {...props1} />;
      case 11:
        return <Exercise11 {...propsWithComments} />;
      case 9:
        return <Exercise9 {...props2} />;
      case 8:
        return <Exercise8 {...props1} showRightAnswerWithComments={showRightAnswerWithComments} />;
      case 7:
        return (
          <AnswerUserFilesProvider>
            <Exercise7 {...props1} />
          </AnswerUserFilesProvider>
        );
      case 6:
        return <Exercise6 {...props1} />;
      case 5:
        return <Exercise5 {...props2} />;
      case 3:
        return <Exercise3 {...props2} />;
      default:
        return <Box>{t("unsupportedExercise", { type: exercise.exercise.type })}</Box>;
    }
  };

  /** */
  const checkAnswerButtonHandler = async () => {
    if (isViewExamDeck) {
      return;
    }
    /** @type {?ExercisePatchData} */
    const answerResult = await checkAnswerRef.current?.();
    if (exercise.deck.exam) {
      info(t("Exercises.answerSaved"));
      patchExercise(answerResult).then();
      return;
    }
    // Неправильные ответы и исчерпание попыток обрабатываются внутри соответствующего типа упражнения.
    if (!answerResult) return;

    // Для типа 8 показываем info, если ответ ещё не выучен; в остальных случаях — success
    const notify = exercise.exercise.type === 8 && !answerResult.learned ? info : success;
    notify(t("Exercises.answerSaved"));
    patchExercise(answerResult).then();
  };

  /** @param {Number} direction */
  const arrowButtonsHandler = (direction) => () => {
    if (specifiedExerciseId) {
      history.push(`/learn/${currentDeck.id}`);
    }
    requestOtherExercise(direction).then();
  };

  if (!exercise) {
    return null;
  }

  let checkButtonText = t("Exercises.check");
  if (homeworkUserData) {
    checkButtonText = t("Exercises.type7.change");
  }
  if (exercise.exercise.type === 6) {
    checkButtonText = t("Exercises.type6.checkButtonText");
  }
  if (exercise.deck.exam) {
    checkButtonText = t("Exercises.examCheck");
  }
  return (
    <Box
      data-component="Exercises"
      flexGrow={1}
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      position="relative"
    >
      <Box data-tag="exercise selector" flexGrow={1}>
        {getExerciseContent()}
      </Box>
      {exercise.exercise.passed && exercise.exercise.learned && !exercise.deck.exam && (
        <CheckCircleRoundedIcon color="primary" className={classes.exerciseLearnStatusIcon} />
      )}
      {exercise.exercise.passed && !exercise.exercise.learned && !exercise.deck.exam && (
        <CancelRoundedIcon className={clsx(classes.exerciseLearnStatusIcon, classes.colorPink)} />
      )}
      <Button className={clsx(classes.arrowButtons)} color="primary" onClick={arrowButtonsHandler(-1)}>
        <ArrowBackIosRoundedIcon />
      </Button>
      <Button
        className={clsx(classes.arrowButtons, classes.nextButton)}
        color="primary"
        onClick={arrowButtonsHandler(0)}
      >
        <ArrowForwardIosRoundedIcon />
      </Button>
      {canShowRightAnswer && (
        <Box display="flex" justifyContent="center" mt={2}>
          <GreyButton onClick={showExamRightAnswer}>{t("Exercises.showRightAnswer")}</GreyButton>
        </Box>
      )}
      <Box
        data-tag="bottom buttons"
        display={isHiddenCheckButton ? "block" : "flex"}
        justifyContent="space-between"
        alignItems="center"
        margin={isHiddenCheckButton ? "15px auto -15px auto" : "15px 0 15px"}
      >
        <GreyButton
          className={classes.button}
          onClick={skipExercise}
          disabled={exercise.deck.exam && (exercise.exercise.passed || exercise.deck.exam.endDate !== null)}
        >
          {t("Exercises.skip")}
        </GreyButton>
        {!exercise.deck.exam && <DefaultInfoBlock exercise={exercise} />}
        {exercise.deck.exam && <ExamInfoBlock exercise={exercise} />}
        {!isHiddenCheckButton && (
          <AddPointsAnimation>
            <PrimaryRoundedButton
              className={classes.button}
              disabled={
                (!exercise.deck.exam && isCheckButtonDisabled) ||
                (exercise.deck.exam &&
                  (exercise.exercise.passed || exercise.deck.exam.endDate !== null || isCheckButtonDisabled))
              }
              onClick={checkAnswerButtonHandler}
            >
              {checkButtonText}
            </PrimaryRoundedButton>
          </AddPointsAnimation>
        )}
      </Box>
    </Box>
  );
};

export default Exercises;
